import { useEffect, useRef } from "react";

/**
 * Call `onVisible` whenever the returned element scrolls into view.
 *
 * The trigger for loading the next month at the foot of a browse view. An
 * IntersectionObserver rather than a scroll handler: it reports off the main
 * thread and needs no throttling, which matters when the thing being scrolled is
 * a few thousand rows.
 *
 * The callback is held in a ref so that changing it — and it changes on every
 * render, being a closure over the current page — does not tear down and rebuild
 * the observer. Only `enabled` does that, which is what stops a load firing
 * while one is already in flight.
 */
export function useOnVisible(onVisible: () => void, enabled = true) {
	const ref = useRef<HTMLDivElement>(null);
	const latest = useRef(onVisible);
	latest.current = onVisible;

	useEffect(() => {
		const el = ref.current;
		if (!el || !enabled) return;

		const observer = new IntersectionObserver((entries) => {
			if (entries.some((e) => e.isIntersecting)) latest.current();
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [enabled]);

	return ref;
}
